import { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';

import { GreaterAreaDropdown } from '@/components/dropdowns/GreaterAreaDropdown';
import { useUpdateChallengePin } from '@/components/challenges/useUpdateChallengePin';
import {
  PinMapEditor,
  type PinMapItem,
  type PinMapLegendEntry,
} from '@/components/shared/map/PinMapEditor';
import { fallbackPinColor, PIN_UNASSIGNED_COLOR } from '@/components/shared/map/pinColors';

/** Lightweight pin returned by GET /challenges/pins. */
interface ChallengePin {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  greaterArea: { id: string; name: string; slug: string; color: string | null } | null;
}

interface LibraryMapEditorProps {
  googleMapsApiKey?: string;
  /** Drag disabled when false. Defaults to true. */
  editable?: boolean;
}

/**
 * One-greater-area-at-a-time map over the standalone challenge library. Showing
 * every area at once overwhelms the map, so the first greater area is selected
 * by default and pins are only fetched for the selected area. Dragging a pin
 * moves the library challenge's base location (PATCH /challenges/:id) — see
 * docs/CHALLENGE_LOCATION_HIERARCHY.md.
 */
export function LibraryMapEditor({ googleMapsApiKey, editable = true }: LibraryMapEditorProps) {
  const apiClient = useApiClient();
  const updatePin = useUpdateChallengePin();
  const [greaterAreaId, setGreaterAreaId] = useState('');

  // Default the filter to the first greater area so the map never loads all
  // areas at once.
  const { data: firstAreaResponse } = useQuery<{ data: Array<{ id: string }> }>({
    queryKey: ['GreaterAreas', 'first'],
    queryFn: () => apiClient.get('/greater-areas?page=1&limit=1'),
    enabled: !greaterAreaId,
  });
  const firstAreaId = firstAreaResponse?.data?.[0]?.id;

  useEffect(() => {
    if (!greaterAreaId && firstAreaId) setGreaterAreaId(firstAreaId);
  }, [greaterAreaId, firstAreaId]);

  const { data } = useQuery<{ success: boolean; data: ChallengePin[] }>({
    queryKey: ['ChallengePins', greaterAreaId],
    queryFn: () => apiClient.get(`/challenges/pins?greaterAreaId=${greaterAreaId}`),
    enabled: Boolean(greaterAreaId),
  });

  const pins = useMemo(() => data?.data ?? [], [data]);

  // Deterministic color per greater area: its own color, else a palette fallback.
  const areaColors = useMemo(() => {
    const ids = Array.from(
      new Set(pins.map((p) => p.greaterArea?.id).filter((id): id is string => Boolean(id))),
    );
    const map = new Map<string, string>();
    ids.forEach((id, index) => {
      const area = pins.find((p) => p.greaterArea?.id === id)?.greaterArea;
      map.set(id, area?.color || fallbackPinColor(index));
    });
    return map;
  }, [pins]);

  const items = useMemo<PinMapItem[]>(
    () =>
      pins.map((pin) => ({
        kind: 'challenge' as const,
        id: pin.id,
        name: pin.name,
        latitude: pin.latitude,
        longitude: pin.longitude,
        color: pin.greaterArea ? (areaColors.get(pin.greaterArea.id) ?? PIN_UNASSIGNED_COLOR) : PIN_UNASSIGNED_COLOR,
        groupName: pin.greaterArea?.name,
      })),
    [pins, areaColors],
  );

  const legend = useMemo<PinMapLegendEntry[]>(() => {
    const present = new Map<string, string>();
    let hasNoArea = false;
    for (const pin of pins) {
      if (pin.greaterArea) present.set(pin.greaterArea.name, areaColors.get(pin.greaterArea.id) ?? PIN_UNASSIGNED_COLOR);
      else hasNoArea = true;
    }
    const entries: PinMapLegendEntry[] = Array.from(present, ([label, color]) => ({ label, color }));
    if (hasNoArea) entries.push({ label: 'No area', color: PIN_UNASSIGNED_COLOR });
    return entries;
  }, [pins, areaColors]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Greater area</span>
        <div className="w-64">
          <GreaterAreaDropdown
            value={greaterAreaId}
            onChange={setGreaterAreaId}
            placeholder="Select greater area"
          />
        </div>
      </div>
      <PinMapEditor
        items={items}
        editable={editable}
        legend={legend}
        fillViewport
        googleMapsApiKey={googleMapsApiKey}
        onMove={(item, latitude, longitude) =>
          updatePin.mutate({ challengeId: item.id, latitude, longitude })
        }
      />
    </div>
  );
}
